import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';

import { listApprovalMonitor } from '../../modules/approvalModule/approvalMonitor';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import ApprStatusChip from '../../components/common/datagrid/ApprStatusChip';
import ApprovalListBox from '../../components/approval_monitoring/ApprovalListBox';
import ApprovalStatusList from '../../components/approval_monitoring/ApprovalStatusList';
import ApprovalList from '../../components/approval_monitoring/ApprovalList';
import { getDefaultSearchRange } from '../../utils/defaultSearchRange';

const ApprovalMonitorListContainer = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  // 검색 시작일/종료일을 비워두지 않고 오늘 기준 앞뒤 1년을 기본 조회범위로 둔다.
  const defaultRange = getDefaultSearchRange();

  const [searchDocTyp, setSearchDocTyp] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('docCd');
  const [searchWords, setSearchWords] = useState(null);
  const [searchStrtDt, setSearchStrtDt] = useState(defaultRange.searchStrtDt);
  const [searchEndDt, setSearchEndDt] = useState(defaultRange.searchEndDt);

  const { approvals, loading } = useSelector(
    ({ approvalModule, loading }) => ({
      approvals: approvalModule.approvalMonitor.approvals,
      loading: loading['approval/LIST_APPROVAL_MONITOR'],
    }),
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID' ,
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'apprUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuUuid',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuCd',
        hide: true,
        type: 'string',
        editable: false,
        sortable: false,
        disableExport: true,
      },
      {
        field: 'docuTyp',
        headerName: '타입',
        minWidth: 120,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'docuNo',
        headerName: '문서번호',
        minWidth: 120,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },

      {
        field: 'partnerInc',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        type: 'string',
        editable: false,
      },
      {
        field: 'docuNow',
        headerName: '결재 단계',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => `${params.row.docuNow} / ${params.row.docuCnt}`,
      },

      {
        field: 'docuNow',
        headerName: '내 결재 단계',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'docuNowUsr',
        headerName: '현재 결재자',
        minWidth: 120,
        flex: 0.6,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      // {
      //   field: 'docuUsr',
      //   hide: true,
      //   type: 'string',
      //   editable: false,
      //   sortable: false,
      //   disableExport: true,
      // },
      // {
      //     field: 'docuUsrDptNm',
      //     headerName: '부서',
      //     minWidth: 200,
      //     flex: 1,
      //     type: 'string',
      //     editable: false,
      //     align: 'center',
      //     headerAlign: 'center',
      // },
      // {
      //     field: 'docuUsrNm',
      //     headerName: '이름',
      //     minWidth: 120,
      //     flex: 0.6,
      //     type: 'string',
      //     editable: false,
      //     align: 'center',
      //     headerAlign: 'center',
      // },
      // {
      //     field: 'docuUsrGradeNm',
      //     headerName: '직급',
      //     minWidth: 80,
      //     flex: 0.4,
      //     type: 'string',
      //     editable: false,
      //     align: 'center',
      //     headerAlign: 'center',
      // },
      {
        field: 'DOCU_STATUS',
        headerName: '결재여부',
        minWidth: 150,
        flex: 0.7,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ApprStatusChip,
      },
      // {
      //     field: 'docuTxt',
      //     headerName: '기본사항',
      //     minWidth: 120,
      //     flex: 0.6,
      //     type: 'string',
      //     editable: false,
      //     align: 'center',
      //     headerAlign: 'center',
      // },
      {
        field: 'crtNm',
        headerName: '작성자',
        minWidth: 80,
        flex: 0.3,
        type: 'string',
        editable: false,
      },
      {
        field: 'crtDt',
        headerName: '작성일',
        minWidth: 150,
        flex: 0.8,
        type: 'string',
        editable: false,
      },
    ],
    [],
  );

  const handleRowClick = (param, event) => {
    navigate(`/approvalmng/monitoring/${param.row.docuUuid}`);
  };

  const searchList = (values) => {
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      searchDocTyp: searchDocTyp,
      [values.searchKeyword]: values.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    setSearchKeyword(values.searchKeyword);
    setSearchWords(values.searchWords);
    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    dispatch(listApprovalMonitor(searchData));
  };

  const initialValues = {
    searchKeyword: searchKeyword,
    searchWords: searchWords,
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  const onChangeDocTyp = (type) => {
    if (type === searchDocTyp) {
      type = '';
    }
    setSearchDocTyp(type);
  };

  // 컴포넌트 마운트 후 list 데이터 요청
  useEffect(() => {
    searchList(formik.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDocTyp]);

  
  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재 모니터링" />
      <ApprovalListBox formik={formik} />
      <ApprovalStatusList
        searchDocTyp={searchDocTyp}
        onChangeDocTyp={onChangeDocTyp}
      />
      <ApprovalList
        columns={columns}
        rows={approvals}
        loading={loading}
        handleRowClick={handleRowClick}
        columnVisibilityModel={{
          // Hide columns status and traderName, the other columns will remain visible
          id: false,
          apprUuid: false,
          docuUuid: false,
          docuCd: false,
          docuTyp: false,
        }} 

      />
    </>
  );
};

export default ApprovalMonitorListContainer;
